export class DifficultyManager {
  // Default distance thresholds for difficulty transitions (in world units).
  // A map may override these via its `difficulty` field, but every map ships
  // with this same progression unless it deliberately opts out.
  static DEFAULT_THRESHOLDS = {
    level1: 0, // Start with level 1 templates
    level2: 100, // Start mixing in level 2 templates at 100 units
    level3: 300, // Start mixing in level 3 templates at 300 units
    level4: 600, // Start mixing in level 4 (brutal) templates at 600 units
    level5: 900, // Start mixing in level 5 (insane) templates at 900 units
  };

  static DEFAULT_PHASE_OUT = {
    level1: 300, // Stop using level 1 templates at 300 units
    level2: 600, // Stop using level 2 templates at 600 units
    level3: 1200, // Stop using level 3 templates at 1200 units
    level4: 1800, // Stop using level 4 templates at 1800 units
  };

  /**
   * @param {Object|null} [config] - optional per-map progression override.
   *   `{ thresholds?, phaseOut? }` are shallow-merged over the defaults, so a
   *   map can nudge a single tier without restating the whole curve. Omit it
   *   (the usual case) to use the shared default progression.
   */
  constructor(config = null) {
    // Initialize difficulty settings
    this.playerDistance = 0;

    // Distance thresholds for difficulty transitions (in world units)
    this.difficultyThresholds = {
      ...DifficultyManager.DEFAULT_THRESHOLDS,
      ...(config && config.thresholds ? config.thresholds : {}),
    };

    // Probability weights for each difficulty level
    // These values will be dynamically adjusted based on player's distance
    this.difficultyWeights = {
      level1: 1.0,
      level2: 0.0,
      level3: 0.0,
      level4: 0.0,
      level5: 0.0,
    };

    // Maximum difficulty weights - will gradually reach these values
    this.maxDifficultyWeights = {
      level1: 1.0,
      level2: 1.0,
      level3: 1.0,
      level4: 1.0,
      level5: 1.0,
    };

    // When to completely phase out easier difficulties
    this.phaseOutThresholds = {
      ...DifficultyManager.DEFAULT_PHASE_OUT,
      ...(config && config.phaseOut ? config.phaseOut : {}),
    };
  }

  // Update difficulty based on current player distance
  updateDifficulty(distance) {
    this.playerDistance = distance;

    // Calculate weight for each difficulty level based on distance
    // Level 1 difficulty
    if (distance >= this.phaseOutThresholds.level1) {
      // Phase out level 1 templates completely
      this.difficultyWeights.level1 = 0;
    } else if (distance >= this.difficultyThresholds.level2) {
      // Gradually reduce level 1 templates as distance increases
      const level1Progress =
        (distance - this.difficultyThresholds.level2) /
        (this.phaseOutThresholds.level1 - this.difficultyThresholds.level2);
      this.difficultyWeights.level1 = Math.max(0, 1 - level1Progress);
    } else {
      // Full weight for level 1 templates at start
      this.difficultyWeights.level1 = this.maxDifficultyWeights.level1;
    }

    // Level 2 difficulty
    if (distance >= this.phaseOutThresholds.level2) {
      // Phase out level 2 templates completely
      this.difficultyWeights.level2 = 0;
    } else if (distance >= this.difficultyThresholds.level3) {
      // Gradually reduce level 2 templates as distance increases
      const level2Progress =
        (distance - this.difficultyThresholds.level3) /
        (this.phaseOutThresholds.level2 - this.difficultyThresholds.level3);
      this.difficultyWeights.level2 = Math.max(0, 1 - level2Progress);
    } else if (distance >= this.difficultyThresholds.level2) {
      // Gradually increase level 2 templates from level 2 threshold to level 3 threshold
      const level2Progress =
        (distance - this.difficultyThresholds.level2) /
        (this.difficultyThresholds.level3 - this.difficultyThresholds.level2);
      this.difficultyWeights.level2 = Math.min(
        this.maxDifficultyWeights.level2,
        level2Progress
      );
    } else {
      // No level 2 templates at start
      this.difficultyWeights.level2 = 0;
    }

    // Level 3 difficulty
    if (distance >= this.phaseOutThresholds.level3) {
      // Phase out level 3 templates completely
      this.difficultyWeights.level3 = 0;
    } else if (distance >= this.difficultyThresholds.level4) {
      // Gradually reduce level 3 templates as distance increases
      const level3Progress =
        (distance - this.difficultyThresholds.level4) /
        (this.phaseOutThresholds.level3 - this.difficultyThresholds.level4);
      this.difficultyWeights.level3 = Math.max(0, 1 - level3Progress);
    } else if (distance >= this.difficultyThresholds.level3) {
      // Gradually increase level 3 templates from level 3 threshold to level 4 threshold
      const level3Progress =
        (distance - this.difficultyThresholds.level3) /
        (this.difficultyThresholds.level4 - this.difficultyThresholds.level3);
      this.difficultyWeights.level3 = Math.min(
        this.maxDifficultyWeights.level3,
        level3Progress
      );
    } else {
      // No level 3 templates before level 3 threshold
      this.difficultyWeights.level3 = 0;
    }

    // Level 4 difficulty
    if (distance >= this.phaseOutThresholds.level4) {
      // Phase out level 4 templates completely
      this.difficultyWeights.level4 = 0;
    } else if (distance >= this.difficultyThresholds.level5) {
      // Gradually reduce level 4 templates as distance increases
      const level4Progress =
        (distance - this.difficultyThresholds.level5) /
        (this.phaseOutThresholds.level4 - this.difficultyThresholds.level5);
      this.difficultyWeights.level4 = Math.max(0, 1 - level4Progress);
    } else if (distance >= this.difficultyThresholds.level4) {
      // Gradually increase level 4 templates from level 4 threshold to level 5 threshold
      const level4Progress =
        (distance - this.difficultyThresholds.level4) /
        (this.difficultyThresholds.level5 - this.difficultyThresholds.level4);
      this.difficultyWeights.level4 = Math.min(
        this.maxDifficultyWeights.level4,
        level4Progress
      );
    } else {
      // No level 4 templates before level 4 threshold
      this.difficultyWeights.level4 = 0;
    }

    // Level 5 (insane) difficulty
    if (distance >= this.difficultyThresholds.level5) {
      // Gradually increase level 5 templates after level 5 threshold
      const level5Progress =
        (distance - this.difficultyThresholds.level5) /
        (this.phaseOutThresholds.level4 - this.difficultyThresholds.level5);
      this.difficultyWeights.level5 = Math.min(
        this.maxDifficultyWeights.level5,
        level5Progress
      );
    } else {
      // No level 5 templates before level 5 threshold
      this.difficultyWeights.level5 = 0;
    }
  }

  // Select a difficulty level based on current weights
  selectDifficulty() {
    // Calculate total weight across all difficulty levels
    const totalWeight =
      this.difficultyWeights.level1 +
      this.difficultyWeights.level2 +
      this.difficultyWeights.level3 +
      this.difficultyWeights.level4 +
      this.difficultyWeights.level5;

    // If no difficulties are available (shouldn't happen), default to level 3
    if (totalWeight <= 0) {
      return "level3";
    }

    // Generate a random value between 0 and totalWeight
    const random = Math.random() * totalWeight;

    // Determine which difficulty range the random value falls into
    let cumulativeWeight = 0;

    cumulativeWeight += this.difficultyWeights.level1;
    if (random < cumulativeWeight) {
      return "level1";
    }

    cumulativeWeight += this.difficultyWeights.level2;
    if (random < cumulativeWeight) {
      return "level2";
    }

    cumulativeWeight += this.difficultyWeights.level3;
    if (random < cumulativeWeight) {
      return "level3";
    }

    cumulativeWeight += this.difficultyWeights.level4;
    if (random < cumulativeWeight) {
      return "level4";
    }

    return "level5";
  }

  // Get the current difficulty weights (for debugging/UI)
  getDifficultyWeights() {
    return { ...this.difficultyWeights };
  }

  // Get the maximum unlocked difficulty level based on weights
  getMaxUnlockedDifficulty() {
    // Check from highest to lowest difficulty
    if (this.difficultyWeights.level5 > 0) return "level5";
    if (this.difficultyWeights.level4 > 0) return "level4";
    if (this.difficultyWeights.level3 > 0) return "level3";
    if (this.difficultyWeights.level2 > 0) return "level2";
    return "level1"; // Default to level 1 if no weights are positive
  }

  // Reset difficulty to initial state
  reset() {
    this.playerDistance = 0;
    this.difficultyWeights = {
      level1: 1.0,
      level2: 0.0,
      level3: 0.0,
      level4: 0.0,
      level5: 0.0,
    };
  }
}
